// components/ProgressSection.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { startOfMonth, endOfMonth } from 'date-fns';
import { calculateScore } from '../lib/scoring';
import { CalendarCheck, Flame, CheckSquare } from 'lucide-react';
import { SectionAccordion } from './SectionAccordion';

export default function ProgressSection({ open, toggle, sectionKey }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;

      const today = new Date();
      const start = startOfMonth(today);
      const end = endOfMonth(today);

      const [journalRes, lessonsRes, tasksRes, goalsRes, profileRes, growthDaysRes] = await Promise.all([
        supabase.from('journal_entries').select('*').eq('user_id', userId),
        supabase.from('lessons_learned').select('*').eq('user_id', userId),
        supabase.from('tasks').select('*').eq('user_id', userId),
        supabase.from('previous_goals').select('*').eq('user_id', userId),
        supabase.from('profiles').select('created_at').eq('id', userId).single(),
        supabase.from('growth_days')
          .select('date, journaled, task_completed, lesson_learned')
          .eq('user_id', userId)
          .gte('date', start.toISOString().slice(0, 10))
          .lte('date', end.toISOString().slice(0, 10))
      ]);

      const signupDate = new Date(profileRes.data?.created_at);
      const daysInMonth = end.getDate();
      const joinedThisMonth = signupDate.getMonth() === today.getMonth() && signupDate.getFullYear() === today.getFullYear();

      const calculated = calculateScore(
        journalRes.data || [],
        lessonsRes.data || [],
        tasksRes.data || [],
        goalsRes.data || []
      );

      console.log('🧮 Calculated Score:', calculated);

      const score = {
        ...calculated,
        taskCount: calculated.completedActions || 0,
        completedGoals: calculated.completedProjects || 0,
        fiftyForFiftyCount: (growthDaysRes.data || []).filter(d => d.journaled && d.task_completed && d.lesson_learned).length,
        daysInMonth: joinedThisMonth ? daysInMonth - signupDate.getDate() + 1 : daysInMonth
      };

      setScore(score);
      setLoading(false);
    };

    fetchData();
  }, []);

  return (
    <SectionAccordion
      title="Progress"
      icon={<CalendarCheck className="w-5 h-5 text-[#004060]" />}
      open={open}
      toggle={toggle}
      sectionKey={sectionKey} 
    >
      {loading ? (
        <p className="text-gray-500">Loading progress...</p>
      ) : (
        <div className="grid gap-y-2 grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          {/* Streaks */}
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="flex items-center gap-2 font-semibold text-[#004060]">
              <Flame className="w-5 h-5" /> Streaks
            </h3>
            <div className="mt-2">
              <p>Journal: <span className="font-bold text-[#004060]">{score.journalStreak} days</span></p>
              <p>Action: <span className="font-bold text-[#004060]">{score.actionStreak} days</span></p>
              <p>Lesson: <span className="font-bold text-[#004060]">{score.lessonsStreak} days</span></p>
            </div>
          </div>

          {/* Actions Completed */}
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-800">
              <CheckSquare className="w-5 h-5 text-[#004060]" /> Actions Completed
            </h3>
            <div className="mt-2">
              <p className="text-2xl font-bold text-[#004060]">{score.taskCount}</p>
            </div>
          </div>
        </div>
      )}
    </SectionAccordion>
  );
}